(() => {
  'use strict';

  if (window.__insmiaCartLineToolsLoaded) return;
  window.__insmiaCartLineToolsLoaded = true;

  const STORAGE_KEY = 'insmia:cart-line-prices:v1';
  const STORAGE_MAX_AGE = 30 * 24 * 60 * 60 * 1000;
  const PRICE_FORMATTER = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  });

  const getRoute = (name, fallback) => window.Theme?.routes?.[name] || fallback;

  const savePriceRecords = (records) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ version: 1, records }));
    } catch (error) {
      console.warn('[INSMIA] Unable to save cart price history.', error);
    }
  };

  const readPriceRecords = () => {
    let records = {};

    try {
      const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
      if (parsed?.version === 1 && parsed.records && typeof parsed.records === 'object') {
        records = parsed.records;
      }
    } catch (error) {
      records = {};
    }

    const now = Date.now();
    let changed = false;
    for (const [variantId, record] of Object.entries(records)) {
      if (
        !record ||
        !Number.isFinite(Number(record.price)) ||
        !Number.isFinite(Number(record.addedAt)) ||
        now - Number(record.addedAt) > STORAGE_MAX_AGE
      ) {
        delete records[variantId];
        changed = true;
      }
    }

    if (changed) savePriceRecords(records);
    return records;
  };

  const getCartSections = () =>
    [...document.querySelectorAll('cart-items-component')]
      .map((component) => component.dataset.sectionId)
      .filter(Boolean)
      .filter((sectionId, index, sectionIds) => sectionIds.indexOf(sectionId) === index);

  const getCartRequestContext = () => ({
    sections: getCartSections().join(','),
    sections_url: window.location.pathname,
  });

  const postCartJson = async (url, body) => {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    });

    let data = {};
    try {
      data = await response.json();
    } catch (error) {
      throw new Error('The cart returned an invalid response.');
    }

    if (!response.ok || data?.status || data?.errors) {
      const detail = data?.description || data?.message || data?.errors;
      const message = typeof detail === 'string' ? detail : 'The cart could not be updated.';
      throw new Error(message);
    }

    return data;
  };

  const dispatchCartUpdate = (resource) => {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource,
          data: {
            source: 'insmia-variant-switch',
            sections: resource?.sections,
          },
        },
      })
    );
  };

  const updateQuantityDeleteLabels = () => {
    document.querySelectorAll('.cart-items__table-row[data-quantity]').forEach((row) => {
      const minusButton = row.querySelector('.quantity-minus');
      if (!(minusButton instanceof HTMLButtonElement)) return;

      if (!minusButton.dataset.insmiaDecreaseLabel) {
        minusButton.dataset.insmiaDecreaseLabel =
          minusButton.getAttribute('aria-label') || 'Decrease quantity';
      }

      const deleteControl = row.querySelector('.insmia-quantity-selector-with-delete');
      const canRemove =
        deleteControl?.dataset.insmiaCanUpdate !== 'false' && !row.querySelector('[data-insmia-remove-disabled]');
      const isSingleItem = Number(row.dataset.quantity) === 1;
      if (isSingleItem && canRemove) {
        minusButton.disabled = false;
        minusButton.setAttribute('aria-label', row.dataset.insmiaRemoveLabel || 'Remove item');
      } else {
        minusButton.setAttribute('aria-label', minusButton.dataset.insmiaDecreaseLabel);
      }
    });
  };

  const refreshPriceDropLabels = () => {
    const records = readPriceRecords();

    document.querySelectorAll('[data-insmia-current-price]').forEach((row) => {
      const priceDrop = row.querySelector('[data-insmia-price-drop]');
      if (!priceDrop) return;

      const currentPrice = Number(row.dataset.insmiaCurrentPrice);
      const record = records[String(row.dataset.variantId)];
      const addedPrice = Number(record?.price);
      const difference = addedPrice - currentPrice;

      if (Number.isFinite(difference) && difference > 0) {
        priceDrop.textContent = `Price dropped ${PRICE_FORMATTER.format(difference / 100)} since you added`;
        priceDrop.hidden = false;
      } else {
        priceDrop.textContent = '';
        priceDrop.hidden = true;
      }
    });
  };

  /**
   * 合并行的数量控件必须由我们独占。
   *
   * 🔴 只靠捕获阶段的 stopImmediatePropagation **拦不住**主题的声明式绑定：
   * 2026-09-20 实测点一次减号，网络面板上同时出现
   *   /cart/update.js（我们的）和 /cart/change（主题的 on:click="/decreaseQuantity"），
   * 两个请求对同一个车互相打架。
   * 主题的 on:click / on:blur 是按**属性**分发的 —— 把属性摘掉就彻底静音了
   * （实测摘掉后只剩我们那一个请求）。morph 重渲染后属性会回来，
   * 所以每次 cart:update 之后都要重新摘一遍，这正是 refreshLineTools 的时机。
   */
  // 现在合并行由 Liquid 直接渲染成无绑定的控件，这里只是兜底：
  // 万一将来哪条路径又渲染出带 on: 属性的原生控件，仍然把它静音掉。
  const neutralizeMergedRowControls = () => {
    document.querySelectorAll('.cart-items__table-row[data-insmia-lines]').forEach((row) => {
      if (Number(row.dataset.insmiaLines || '1') <= 1) return;
      row.querySelectorAll('.quantity-minus, .quantity-plus').forEach((element) => {
        element.removeAttribute('on:click');
      });
      // 主题的数量框已经不叫 updates[] 了（见 cart-products.liquid：结算时会按位置错套数量），认新标记
      row.querySelectorAll('input[data-insmia-theme-qty], input[name="updates[]"]').forEach((element) => {
        element.removeAttribute('on:blur');
        element.removeAttribute('on:focus');
      });
    });
  };

  const refreshLineTools = () => {
    neutralizeMergedRowControls();
    upgradeVariantControls();
    watchCartDrawer();
    watchQuickAddDialog();
    updateQuantityDeleteLabels();
    refreshPriceDropLabels();
  };

  const scheduleLineToolsRefresh = () => {
    window.setTimeout(refreshLineTools, 0);
    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(() => window.requestAnimationFrame(refreshLineTools));
    }
  };

  const rememberAddedPrices = (event) => {
    const data = event.detail?.data || {};
    if (data.didError) return;

    const items = event.detail?.resource?.items;
    if (!Array.isArray(items) || items.length === 0) return;

    const sourceId = event.detail?.sourceId;
    const requestedVariantId = String(data.variantId || sourceId || '');
    const records = readPriceRecords();
    const explicitItem = items.find(
      (item) => /^\d+$/.test(requestedVariantId) && String(item.variant_id || item.id) === requestedVariantId
    );
    const source = String(data.source || '');
    const targetItems = explicitItem
      ? [explicitItem]
      : source === 'product-form-component' || source === 'quick-order-quantity'
        ? items.filter((item) => !records[String(item.variant_id || item.id)])
        : [];

    if (targetItems.length === 0) return;

    let changed = false;
    for (const item of targetItems) {
      const variantId = String(item.variant_id || item.id || '');
      // 🔴 记折前单价：final_price 带着第二双折扣，拿它当「加购时的价」会让降价提示失真（2026-09-28）
      const price = Number(item.original_price ?? item.price ?? item.final_price);
      if (!variantId || !Number.isFinite(price)) continue;

      records[variantId] = {
        price,
        addedAt: Date.now(),
      };
      changed = true;
    }

    if (changed) savePriceRecords(records);
  };

  const parseProperties = (row) => {
    if (!row.dataset.insmiaProperties) return undefined;

    try {
      const properties = JSON.parse(row.dataset.insmiaProperties);
      // 🔴 Liquid 的 `item.properties | json` 在**没有属性时输出 `[]` 而不是 `{}`**，
      // 而 typeof [] === 'object' 会让空数组通过这道判断。Shopify /cart/add.js 收到数组会报
      // `expected Array to be a Hash: properties` → 加购失败，连带回滚也失败 → 商品凭空消失。
      // 所以必须排除数组，并且空对象也不传（传 {} 无意义）。
      if (!properties || typeof properties !== 'object' || Array.isArray(properties)) return undefined;
      return Object.keys(properties).length > 0 ? properties : undefined;
    } catch (error) {
      return undefined;
    }
  };

  const buildCartItem = (row, variantId) => {
    const numericVariantId = Number(variantId);
    const quantity = Math.max(1, Number.parseInt(row.dataset.quantity || '1', 10));
    if (!Number.isFinite(numericVariantId)) throw new Error('The selected variant is invalid.');

    const item = {
      id: numericVariantId,
      quantity,
    };
    const properties = parseProperties(row);
    if (properties) item.properties = properties;
    if (row.dataset.insmiaSellingPlan) item.selling_plan = row.dataset.insmiaSellingPlan;
    return item;
  };

  const setVariantError = (row, message) => {
    const errorElement = row.querySelector('[data-insmia-variant-error]');
    if (!errorElement) return;
    errorElement.textContent = message;
    errorElement.hidden = false;
  };

  /**
   * 换变体失败时把错误写到那一行上。
   *
   * 🔴 不能直接写当前这个 row 节点：失败分支会先把旧商品加回购物车、触发 morph，
   * 行节点整个被换掉，刚写上去的提示当场蒸发（实测 rowError 是空字符串）。
   * 所以按变体 id 重新找行，并且在 morph 前后各写一次 —— 写的是同一句话，
   * 重复写没有副作用，但能保证最后落在活着的那个节点上。
   */
  const reportVariantError = (variantId, message) => {
    const write = () => {
      const row = document.querySelector(`.cart-items__table-row[data-variant-id="${variantId}"]`);
      if (row) setVariantError(row, message);
    };
    write();
    for (const delay of [300, 900]) window.setTimeout(write, delay);
  };

  const setLineError = (row, message) => {
    const errorText = row.querySelector('.cart-item__error-text');
    const errorContainer = row.querySelector('.cart-items__error');
    if (!(errorText instanceof HTMLElement) || !(errorContainer instanceof HTMLElement)) return;
    errorText.textContent = message;
    errorContainer.classList.remove('hidden');
  };

  /** 该行是不是「同变体多行合并出来的」—— Liquid 写在 data-insmia-lines 上 */
  const isMergedRow = (row) => Number(row?.dataset.insmiaLines || '1') > 1;

  /** 这一行的数量控件是不是我们自己渲染的（2026-09-27 起所有行都是，见 cart-products.liquid） */
  const ownsQuantity = (row) => !!row?.querySelector('[data-insmia-qty]');

  /**
   * 把某个变体在购物车里的**总数量**设成 quantity。
   *
   * 🔴 为什么不用 /cart/change.js：它只认单个行项目（line/id），而 Shopify 的
   * 「第二双 40% off」(BXGY) 会把同一个变体拆成两个行项目（实测一行 $80.99
   * 未打折、一行 $48.60 打折后）。按行改只会动到其中一行，另一行原封不动，
   * 用户看到的就是「点了减号但东西只少了一半」。
   * /cart/update.js 的 updates 以 variant_id 为键，语义正好是「这个变体一共要几件」，
   * 拆行/合行交给 Shopify 自己重算，折扣也跟着重新分配。
   */
  /* 价格「正在重算」的闪烁：这一行的价格 + 合计金额（主题 text-component 自带 shimmer，主题自己改数量时也这样闪）。
     落地时 morph 按服务端 HTML 同步属性会自然去掉；失败时没有 morph，要手动去掉。 */
  const priceShimmer = (row, on) => {
    const targets = [
      ...row.querySelectorAll('.cart-items__price text-component'),
      ...document.querySelectorAll('text-component[ref="cartTotal"]'),
    ];
    targets.forEach((el) => el.toggleAttribute('shimmer', on));
  };

  /**
   * 合并行（同一变体被第二双折扣拆成的几行）改数量：一次 update.js，{第一行key: 总数, 其余key: 0}。
   * 🔴 以前先 GET /cart.js 再按行序拼数组 = 两次往返，价格要 1.7 秒才变（2026-09-28 实测）。
   * 行 key 各不相同，按 key 改不会像按变体 id 那样静默 no-op；key 过期时 Shopify 按 key 里的变体 id 去找，
   * 实测 1→3、3→1、1→2、拿过期 key 改成 4 都对，拆行交给 Shopify 自己重算。失败返回 null，调用方走原路径。
   */
  const setMergedQuantityByKeys = async (row, quantity) => {
    const keys = String(row.dataset.insmiaKeys || '').split(/\s+/).filter(Boolean);
    if (keys.length < 2) return null;
    try {
      return await postCartJson(getRoute('cart_update_url', '/cart/update.js'), {
        ...getCartRequestContext(),
        updates: Object.fromEntries(keys.map((key, index) => [key, index === 0 ? Math.max(0, quantity) : 0])),
      });
    } catch {
      return null;
    }
  };

  const setVariantQuantity = async (row, quantity, control) => {
    if (row.dataset.insmiaUpdating === 'true') {
      /* 🔴 请求还在路上时**不能丢掉**新的数量。以前这里直接 return：连点时后面几下被吞，
         服务端结果回来又把输入框刷回旧值，看起来像「点了没反应 / 数字往回跳」。
         记下最后一次想要的数量，等这一趟回来立刻补发（见 finally）。 */
      row.__insmiaQueuedQty = quantity;
      return;
    }
    const variantId = row.dataset.variantId;
    if (!variantId) return;

    row.dataset.insmiaUpdating = 'true';
    row.classList.add('insmia-pending-out');
    priceShimmer(row, true);
    // 🔴 不再禁用按钮：禁用会挡住请求期间的连点（上面已经改成排队补发）

    try {
      if (isMergedRow(row)) {
        const mergedCart = await setMergedQuantityByKeys(row, quantity);
        if (mergedCart) {
          window.insmiaCartFlip?.capture();
          dispatchCartUpdate(mergedCart);
          window.insmiaCartFlip?.play();
          return;
        }
      }

      /* 快路：普通单行直接按行项目 key 用**一次** /cart/change.js（主题原来也是一次请求）。
         key 过期（Shopify 重新拆行后会变）时这一步会报错，退回下面按 /cart.js 定位的稳妥路径。 */
      if (!isMergedRow(row) && row.dataset.key) {
        let fastCart = null;
        try {
          fastCart = await postCartJson(getRoute('cart_change_url', '/cart/change.js'), {
            ...getCartRequestContext(),
            id: row.dataset.key,
            quantity: Math.max(0, quantity),
          });
        } catch (fastError) {
          fastCart = null;
        }
        if (fastCart) {
          // 标签在行之间挪动时平滑过渡，不要一帧跳过去（见 insmia-cart-select.js 的 FLIP 段）
          window.insmiaCartFlip?.capture();
          dispatchCartUpdate(fastCart);
          window.insmiaCartFlip?.play();
          return;
        }
      }

      /* 🔴🔴 /cart/update.js 的 updates **必须用数组形式**（按行序逐行给数量）。
         对象形式 {variantId: n} 在「多行共用同一个变体」时 Shopify 无法判定改哪一行，
         返回 200 却什么都不做。2026-09-20 对 2 行同变体实测：
           {45328825122886: 1} → 仍然 2 行 2 件（静默 no-op）
           {45328825122886: 0} → 只删掉 1 行，还剩 1 行
           [1, 0]              → 正确变成 1 行 1 件
         而 BXGY（第二双 40% off）必然产生多行同变体，所以对象形式在本店基本不可用。 */
      const cartUrl = getRoute('cart_url', '/cart');
      const current = await (await fetch(cartUrl + '.js')).json();
      const lines = Array.isArray(current.items) ? current.items : [];
      /* 🔴 同一个变体**可能占好几行**：只要有行带自定义属性/父子关系/订阅计划就不合并
         （比如同款同码、一行 Gift:yes 一行 Gift:no）。这时候按 variant 扫全车会把
         **另一行也清零**——点一下加号，另一条属性的那行就没了。
         所以优先按本行自己的行项目 key 来定位（合并行的 data-insmia-keys 是整组）。
         🔴 key 不稳定（实测改一次数量 45917606772806:5f3b… 就变成了 :d379…），
         匹配不上时退回原来的按变体处理，保证常规链路不受影响。 */
      const keySet = new Set((row.dataset.insmiaKeys || row.dataset.key || '').split(/\s+/).filter(Boolean));
      const indexesOf = (predicate) =>
        lines.reduce((acc, line, index) => (predicate(line) ? acc.concat(index) : acc), []);

      let targets = indexesOf((line) => keySet.has(line.key));
      if (targets.length === 0) targets = indexesOf((line) => String(line.variant_id) === variantId);
      if (targets.length === 0) return;

      // 组里第一行拿全部数量，其余清零 —— 拆行/合行交给 Shopify 自己重算
      const updates = lines.map((line) => line.quantity);
      targets.forEach((index, n) => {
        updates[index] = n === 0 ? Math.max(0, quantity) : 0;
      });

      const cart = await postCartJson(getRoute('cart_update_url', '/cart/update.js'), {
        ...getCartRequestContext(),
        updates,
      });
      window.insmiaCartFlip?.capture();
      dispatchCartUpdate(cart);
      window.insmiaCartFlip?.play();
    } catch (error) {
      setLineError(row, error instanceof Error ? error.message : 'Unable to update this item. Please try again.');
      /* 🔴 失败时把输入框改回购物车里的**真实数量**：点击那一刻已经乐观地改成了目标值，
         不改回来就是「显示 3 件、红字说只能买 2 件、实际还是 2 件」，再点加号还会从 3 往上算
         （2026-09-27 模拟 422 实测）。排队中的数量也作废 —— 同样的原因大概率还会失败。
         data-quantity 是服务端渲染的，失败时没有重渲染，它就是真值。 */
      delete row.__insmiaQueuedQty;
      const live = (row.id && document.getElementById(row.id)) || row;
      const input = live.querySelector('[data-insmia-qty-input]');
      if (input && live.dataset.quantity) input.value = live.dataset.quantity;
      priceShimmer(live, false);
    } finally {
      row.classList.remove('insmia-pending-out');
      delete row.dataset.insmiaUpdating;
      if (control && document.contains(control)) control.disabled = false;

      const queued = row.__insmiaQueuedQty;
      if (queued !== undefined) {
        delete row.__insmiaQueuedQty;
        // morph 按行 id 保留了同一个节点；万一被换掉就按 id 找回来
        const live = (row.id && document.getElementById(row.id)) || row;
        const input = live.querySelector('[data-insmia-qty-input]');
        // 服务端结果刚把输入框刷回了上一趟的值，先把用户最后点到的数写回去，再补发
        if (input) input.value = String(queued);
        void setVariantQuantity(live, queued, null);
      }
    }
  };

  const pendingQuantity = new Map();

  /**
   * 数量加减：点一下立刻改输入框的数字，**立刻发请求**；请求在路上时再点的，
   * 由 setVariantQuantity 记下最后一次想要的数量，回来后补发一次（连点三下 = 两次请求，不丢点击）。
   * 🔴 2026-09-28 去掉了原来「先等 320ms 防抖」：单点一下要多等三分之一秒价格才动（用户：价格显示卡顿）。
   *
   * 🔴 之前的写法是「已有请求在飞就整个丢弃这次点击」(setVariantQuantity 开头那道
   * insmiaUpdating 闸)，实测快速点两下加号只加 1 —— 用户会以为点漏了、再点，
   * 于是数量又跳。防抖 + 乐观回显同时解决「不跟手」和「丢点击」。
   */
  const scheduleVariantQuantity = (row, quantity, control) => {
    const variantId = String(row.dataset.variantId || '');
    if (!variantId) return;

    const target = Math.max(0, quantity);
    const input = row.querySelector('[data-insmia-qty-input]');
    if (input) input.value = String(target);

    if (target === 0) row.classList.add('insmia-pending-out'); // 删到 0 立刻给反馈
    priceShimmer(row, true);
    // 请求在路上时 setVariantQuantity 只记下这个数、回来后补发（按行记，同款同码的两行互不覆盖）
    void setVariantQuantity(row, target, control);
  };

  const removeSingleQuantityLine = async (row, button) => {
    if (row.dataset.insmiaRemoving === 'true') return;

    row.dataset.insmiaRemoving = 'true';
    row.classList.add('insmia-pending-out');
    priceShimmer(row, true);
    button.disabled = true;

    try {
      const removedCart = await postCartJson(getRoute('cart_change_url', '/cart/change.js'), {
        ...getCartRequestContext(),
        // 🔴 Shopify /cart/change.js：`line` 是**整数行号**，`id` 才是行 item key 字符串。
        // 传 line:<key> 会报 `expected String to be a Integer: line`，删除和换变体都会静默失败。
        id: row.dataset.key,
        quantity: 0,
      });
      dispatchCartUpdate(removedCart);
    } catch (error) {
      row.classList.remove('insmia-pending-out');
      priceShimmer(row, false);
      button.disabled = false;
      setLineError(row, error instanceof Error ? error.message : 'Unable to remove this item. Please try again.');
    } finally {
      delete row.dataset.insmiaRemoving;
    }
  };

  /**
   * 把某一行换成另一个变体。成功返回 true。
   * 下拉框和尺码弹窗两个入口都走这里 —— 换变体这条链路（合并行整组清零 →
   * 重新加购 → 加购失败回滚原变体）踩过坑，只应该存在一份。
   */
  const switchRowVariant = async (row, newVariantId, control) => {
    const oldVariantId = row.dataset.variantId;
    if (!newVariantId || String(newVariantId) === String(oldVariantId)) return true;

    const context = getCartRequestContext();
    const oldItem = buildCartItem(row, oldVariantId);
    row.classList.add('insmia-variant-switching');
    row.setAttribute('aria-busy', 'true');
    if (control) control.disabled = true;

    try {
      if (isMergedRow(row)) {
        // 合并行代表该变体的所有行项目，必须整组清零。
        // 同样只能用数组形式：{variantId: 0} 在多行同变体时只删得掉一行（实测）。
        const cartUrl = getRoute('cart_url', '/cart');
        const current = await (await fetch(cartUrl + '.js')).json();
        const lines = Array.isArray(current.items) ? current.items : [];
        const clear = lines.map((line) => (String(line.variant_id) === String(oldVariantId) ? 0 : line.quantity));
        /* 🔴 这一步**不要带 sections**。带上的话 Shopify 会把整个购物车区块渲染一遍
           塞进响应，而我们只在最后那次 add 之后才 morph、这份 HTML 直接丢掉 ——
           实测这一个请求就花了 981ms、回了 143,689 字符，纯白等。 */
        await postCartJson(getRoute('cart_update_url', '/cart/update.js'), { updates: clear });
      } else {
        await postCartJson(getRoute('cart_change_url', '/cart/change.js'), {
          // 🔴 Shopify /cart/change.js：`line` 是**整数行号**，`id` 才是行 item key 字符串。
          // 传 line:<key> 会报 `expected String to be a Integer: line`，删除和换变体都会静默失败。
          id: row.dataset.key,
          quantity: 0,
        });
      }

      let addedCart;
      try {
        addedCart = await postCartJson(getRoute('cart_add_url', '/cart/add.js'), {
          ...context,
          items: [buildCartItem(row, newVariantId)],
        });
      } catch (addError) {
        try {
          const restoredCart = await postCartJson(getRoute('cart_add_url', '/cart/add.js'), {
            ...context,
            items: [oldItem],
          });
          dispatchCartUpdate(restoredCart);
          /* 🔴 换不过去必须说出来。弹窗现在是点完就关的，不报错的话用户只会看到
             「弹窗关了、尺码没变、一句话也没有」。真实场景里最常见的原因就是
             想换的那个码没货了，Shopify 的 description 里写得很清楚，直接透给用户。 */
          reportVariantError(
            oldVariantId,
            addError instanceof Error ? addError.message : 'Unable to change this item. Please try again.'
          );
          return false;
        } catch (restoreError) {
          throw new Error('Unable to change this item. Refresh the cart before trying again.');
        }
      }

      /* 🔴 在顺序表里**原地**把旧变体换成新变体，这一行的位置就守住了。
         必须在 dispatchCartUpdate 之前 —— morph 完 paint() 立刻按这张表重排。
         Shopify 的 /cart/add.js 是往前插的，不做这一步换完尺码就跳到第一行。 */
      window.insmiaCartOrder?.rename(oldVariantId, newVariantId);

      dispatchCartUpdate(addedCart);
      return true;
    } catch (error) {
      setVariantError(row, error instanceof Error ? error.message : 'Unable to change this item. Please try again.');
      return false;
    } finally {
      row.classList.remove('insmia-variant-switching');
      row.removeAttribute('aria-busy');
      if (control && document.contains(control)) control.disabled = false;
    }
  };

  /** 下拉框入口（渐进增强的兜底形态）：失败时把选中项拨回去 */
  const changeCartVariant = async (select) => {
    const row = select.closest('.cart-items__table-row[data-key]');
    if (!row) return;

    const previous = row.dataset.variantId;
    const ok = await switchRowVariant(row, select.value, select);
    if (!ok) select.value = previous;
  };

  /* ───────── 换尺码/颜色：直接复用主题原生的 quick-add 弹窗 ───────── */
  /*
   * 用户 2026-09-20 拍板：换尺码弹出的必须和专辑页 / 推荐位弹出的是**同一个**弹窗
   * （大图 + Color 色卡 + Size 宫格 + SIZE GUIDE），不要另做一个小的。
   * 我先前自己搓了一个紧凑版，被打回，已整段删除。
   *
   * 🔴 原生 quick-add-component 是从 `closest('product-card')` 里 `ref="productCardLink"`
   * 那个链接取产品页地址的。购物车行里没有 product-card，所以 Liquid 给每行渲染了一个
   * 隐藏的最小壳（cart-products.liquid 里的 [data-insmia-vs-host]）。
   * 可见的那颗按钮只负责把点击转发给壳里的 choose 按钮。
   *
   * 🔴 原生弹窗的 CTA 是「加购」，点下去会**新增一行**；这里要的是把当前这行换成另一个变体。
   * 所以在捕获阶段拦下弹窗里表单的 submit 改走 switchRowVariant，
   * 并给弹窗挂 .insmia-vs-switch，由 CSS 把 BUY IT NOW 藏掉、按钮文案改成 Update。
   *
   * 🔴 顺带说明：推荐位的快捷加购**不再拦截**，交还给主题原生。
   * 之前我以为它「静默加默认码」，那是我测错了 —— quick-add.liquid 里
   * add 和 choose 两颗按钮都渲染、靠 data-quick-add-button 决定显示哪颗，
   * 我当时 querySelector('button') 抓到的是隐藏的那颗 add。
   */
  let pendingSwitchRow = null;
  let switchObserver = null;

  const quickAddDialog = () => document.getElementById('quick-add-dialog');

  /**
   * 🔴🔴 主题的 `on:submit="/handleSubmit"` 是 **document 级捕获委托、且比我们的脚本更早注册**，
   * 所以在捕获阶段 stopImmediatePropagation **拦不住它**（和数量按钮那次是同一个坑）。
   * 实测：点 UPDATE 时主题照样把新变体加进车，结果是多出一行而不是替换掉原来那行。
   * ⇒ 在「换变体」模式下把 on:submit 属性摘掉，它就没东西可分发了；
   *   弹窗里换色换码会把表单重渲染、属性跟着回来，所以挂个观察器渲染一次摘一次。
   *   注意属性挂在 <product-form-component> 上，不是 <form> 上。
   */
  const stripQuickAddSubmitBinding = () => {
    // 🔴 不要写 querySelectorAll('[on\\:submit]')：冒号在 CSS 属性选择器里必须转义，
    // 而这段代码经过多层转写很容易把反斜杠吃掉，变成非法选择器 '[on:submit]' 直接抛异常
    // （2026-09-20 实测抛了 SyntaxError，整个换变体模式没起来）。用属性判断，不碰选择器转义。
    const host = quickAddDialog();
    if (!host) return;
    host.querySelectorAll('product-form-component, form').forEach((element) => {
      if (element.hasAttribute('on:submit')) element.removeAttribute('on:submit');
    });
  };

  const startSwitchMode = (row) => {
    const host = quickAddDialog();
    if (!host) return;

    pendingSwitchRow = row;
    host.classList.add('insmia-vs-switch');
    stripQuickAddSubmitBinding();

    switchObserver?.disconnect();
    // 只看 childList/subtree，不看 attributes —— 摘属性本身不会再触发它，不会自激
    switchObserver = new MutationObserver(stripQuickAddSubmitBinding);
    switchObserver.observe(host, { childList: true, subtree: true });
  };

  /**
   * 🔴 原生 quick-add 要先 fetch 一整个产品页才弹窗，冷启动实测 3~6 秒。
   * 这段时间按钮毫无反应，用户只会反复点。这里给它一个加载态，
   * 弹窗真的打开（或超时）再放开。
   */
  let pendingTrigger = null;
  let pendingTriggerTimer = 0;

  const releaseTrigger = () => {
    window.clearTimeout(pendingTriggerTimer);
    if (!pendingTrigger) return;
    pendingTrigger.removeAttribute('aria-busy');
    pendingTrigger.disabled = false;
    pendingTrigger = null;
  };

  const holdTrigger = (trigger) => {
    releaseTrigger();
    pendingTrigger = trigger;
    trigger.setAttribute('aria-busy', 'true');
    trigger.disabled = true;
    // 兜底：产品页拉不回来时也要把按钮放开，别让它永久禁用
    pendingTriggerTimer = window.setTimeout(releaseTrigger, 12000);
  };

  const clearSwitchMode = () => {
    pendingSwitchRow = null;
    switchObserver?.disconnect();
    switchObserver = null;
    releaseTrigger();
    quickAddDialog()?.classList.remove('insmia-vs-switch');
  };

  const watchQuickAddDialog = () => {
    const dialog = quickAddDialog()?.querySelector('dialog');
    if (!dialog || dialog.dataset.insmiaWatched === '1') return;
    dialog.dataset.insmiaWatched = '1';
    /* 🔴 下面那个属性观察器只在 `open` **被设过又被去掉**时才触发。
       万一 showModal 没真的把弹窗开起来（渲染被节流等），`open` 从头到尾不存在，
       关闭时没有属性变化 ⇒「换变体」模式一直挂着。
       原生 close/cancel 事件不依赖这个前提，补一道。 */
    dialog.addEventListener('close', clearSwitchMode);
    dialog.addEventListener('cancel', clearSwitchMode);
    new MutationObserver(() => {
      if (dialog.hasAttribute('open')) {
        // 弹窗真的开了，把触发按钮的加载态放开
        releaseTrigger();
      } else {
        // 关闭时必须清掉「换变体」模式，否则下次从推荐位打开会被当成换变体
        clearSwitchMode();
      }
    }).observe(dialog, { attributes: true, attributeFilter: ['open'] });
  };

  const openVariantDialog = (trigger) => {
    /* 🔴 这里**不能**限定 [data-key]。取消勾选的商品是从购物车里删掉的，
       它的幽灵行没有 data-key，加了这个限定条件按钮就成了死按钮（实测点了没反应）。
       真实行走 /cart 接口，幽灵行改 localStorage 快照 —— 分叉在 submit 那边。 */
    const row = trigger.closest('.cart-items__table-row');
    const opener = row?.querySelector('[data-insmia-vs-open]');
    if (!row || !opener) return;

    startSwitchMode(row);
    watchQuickAddDialog();
    holdTrigger(trigger);
    opener.click();
  };

  /**
   * 🔴 只要这次快捷加购**不是**从购物车行里发起的，就先把「换变体」模式清掉。
   *
   * 后果很严重：`pendingSwitchRow` 一旦残留（弹窗没走正常关闭流程），
   * 推荐位那颗 quick-add 弹出来的加购会被 submit 拦截器当成「替换那一行」——
   * 用户想加一双新鞋，结果是把购物车里另一双换掉了。
   * 这一道在 fetch 产品页之前就跑，所以新渲染出来的弹窗不会再被摘 on:submit，
   * 主题自己的加购链路完好。
   */
  document.addEventListener(
    'click',
    (event) => {
      if (!pendingSwitchRow) return;
      const target = event.target instanceof Element ? event.target : null;
      if (!target || !target.closest('quick-add-component')) return;
      // 购物车行自己的隐藏开关和触发按钮除外，那正是「换变体」该走的路
      if (target.closest('[data-insmia-vs-open]') || target.closest('[data-insmia-variant-trigger]')) return;
      clearSwitchMode();
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      if (!pendingSwitchRow) return;

      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.closest('#quick-add-dialog')) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      const row = pendingSwitchRow;
      const variantId = form.querySelector('[name="id"]')?.value;
      const submitButton = form.querySelector('[type="submit"]');
      // 弹窗开着的这段时间里购物车可能被别处改过，行没了就别硬换
      if (!variantId || !document.contains(row)) {
        clearSwitchMode();
        quickAddDialog()?.closeDialog?.();
        return;
      }

      /* 🔴 先关弹窗，再发请求。用户的选择已经表达完了，没必要让他盯着一个
         禁用的 UPDATE 按钮干等 —— 实测「点 UPDATE 到弹窗关闭」2,065ms
         （change.js 981ms + add.js 898ms 串行）。
         换变体本身仍然要等服务端，但反馈落在那一行上：switchRowVariant 会给行加
         insmia-variant-switching / aria-busy，失败时写行内错误。 */
      clearSwitchMode();
      quickAddDialog()?.closeDialog?.();

      /* 幽灵行不在购物车里，没有行项目可改 —— 它要改的是 localStorage 里的快照。
         快照逻辑在 insmia-cart-select.js（模块作用域互不可见），用事件过桥。 */
      if (row.classList.contains('insmia-cart-ghost')) {
        document.dispatchEvent(
          new CustomEvent('insmia:ghost-variant', {
            detail: {
              oldVariantId: row.dataset.variantId,
              variantId: String(variantId),
              handle: row.querySelector('[data-insmia-variant-trigger]')?.dataset.handle || '',
            },
          })
        );
        return;
      }

      void switchRowVariant(row, String(variantId), submitButton);
    },
    true
  );

  /**
   * 渐进增强：Liquid 里下拉框和触发按钮都渲染了，CSS 默认显示下拉框。
   *
   * 🔴 状态挂在 <html> 上、由 CSS 决定显隐，**不要**逐个元素去 set hidden：
   * 抽屉每次打开主题都会重渲染购物车内容，morph 出来的新按钮没经过 JS，
   * 又会变回隐藏（2026-09-20 实测：开抽屉后按钮消失、下拉框回来了）。
   * 一个 <html> 类不受 section 重渲染影响，一次设置永久生效。
   */
  const upgradeVariantControls = () => {
    document.documentElement.classList.add('insmia-vs-ready');
  };

  /**
   * 抽屉打开时主题会重渲染一次购物车内容，新节点没经过 refreshLineTools。
   * 🔴 这里只观察 <dialog> 的 open **属性**（attributes-only，没有 subtree）——
   * 在 document.body 上挂 subtree 观察器曾把点击延迟拖到 1.1 秒，不能重蹈覆辙。
   */
  const watchCartDrawer = () => {
    document.querySelectorAll('cart-drawer-component dialog').forEach((dialog) => {
      if (dialog.dataset.insmiaWatched === '1') return;
      dialog.dataset.insmiaWatched = '1';
      new MutationObserver(scheduleLineToolsRefresh).observe(dialog, {
        attributes: true,
        attributeFilter: ['open'],
      });
    });
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target instanceof Element ? event.target.closest('[data-insmia-variant-trigger]') : null;
    if (!trigger) return;
    event.preventDefault();
    openVariantDialog(trigger);
  });


  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      const button = target instanceof Element ? target.closest('.quantity-minus, .quantity-plus') : null;
      if (!(button instanceof HTMLButtonElement)) return;

      const row = button.closest('.cart-items__table-row[data-key]');
      if (!row) return;

      const deleteControl = row.querySelector('.insmia-quantity-selector-with-delete');
      const canUpdate = deleteControl?.dataset.insmiaCanUpdate !== 'false';
      const canRemove = !row.querySelector('[data-insmia-remove-disabled]');
      const isPlus = button.classList.contains('quantity-plus');
      // 🔴 基数取**输入框当前显示的值**而不是 data-quantity：
      // data-quantity 要等服务端回来重渲染才更新，连点时它是旧的，第二下会算错。
      const shown = row.querySelector('[data-insmia-qty-input]');
      const shownValue = shown ? Number.parseInt(shown.value || '', 10) : Number.NaN;
      const current = Number.isFinite(shownValue)
        ? Math.max(0, shownValue)
        : Math.max(0, Number.parseInt(row.dataset.quantity || '0', 10) || 0);

      if (ownsQuantity(row)) {
        // 自有控件（2026-09-27 起所有行）：按 key 定位、320ms 防抖合并连点、在途排队不丢
        if (!canUpdate) return;
        const next = isPlus ? current + 1 : current - 1;
        if (next < 0 || (next === 0 && !canRemove)) return;

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        scheduleVariantQuantity(row, next, button);
        return;
      }

      // 兜底（旧缓存页面上可能还有主题原生控件）：数量为 1 时减号 = 删除（原有行为）
      if (isPlus || current !== 1 || !canUpdate || !canRemove) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      void removeSingleQuantityLine(row, button);
    },
    true
  );

  /**
   * 合并行的数量输入框也要拦下来。
   * 🔴 必须在 document 的**捕获**阶段拦：主题用 `on:blur="/setQuantity"` 绑在组件内部，
   * 捕获阶段从 document 往下走，先于组件自己的监听器，stopPropagation 才拦得住。
   * blur 不冒泡，但捕获阶段照样能收到。
   */
  const interceptMergedQuantityInput = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    // data-insmia-theme-qty = 主题原生控件（原来叫 updates[]，为防结算错套数量已去掉 name）；
    // data-insmia-qty-input = 合并行我们自己渲染的那个
    const isThemeInput = input.hasAttribute('data-insmia-theme-qty') || input.name === 'updates[]';
    if (!isThemeInput && !input.hasAttribute('data-insmia-qty-input')) return;

    const row = input.closest('.cart-items__table-row[data-key]');
    if (!row || !ownsQuantity(row)) return;

    const deleteControl = row.querySelector('.insmia-quantity-selector-with-delete');
    if (deleteControl?.dataset.insmiaCanUpdate === 'false') return;

    const next = Math.max(0, Number.parseInt(input.value || '0', 10) || 0);
    const current = Math.max(0, Number.parseInt(row.dataset.quantity || '0', 10) || 0);
    if (next === current) return;
    if (next === 0 && row.querySelector('[data-insmia-remove-disabled]')) {
      input.value = String(current);
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    scheduleVariantQuantity(row, next, input);
  };

  document.addEventListener('blur', interceptMergedQuantityInput, true);
  document.addEventListener('change', interceptMergedQuantityInput, true);

  document.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLSelectElement) || !target.matches('[data-insmia-variant-switch]')) return;
    void changeCartVariant(target);
  });

  document.addEventListener('cart:update', (event) => {
    rememberAddedPrices(event);
    scheduleLineToolsRefresh();
  });
  document.addEventListener('discount:update', scheduleLineToolsRefresh);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refreshLineTools, { once: true });
  } else {
    refreshLineTools();
  }
})();
